import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Settings, Pencil, ArrowLeft } from 'lucide-react';
import Alert from '../common/Alert.js';
import roleService from '../../services/RoleService.js';
import {
  PageLayout,
  Button,
  DetailCard,
  DetailRow,
} from '../common/index.js';

const ViewRole = () => {
  const { roleId } = useParams();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRole();
  }, [roleId]);

  const fetchRole = async () => {
    try {
      const response = await roleService.getById(roleId);
      setRole(response.data);
    } catch (err) {
      Alert.error('Failed to load role details');
      console.error(err);
      navigate('/roles');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <PageLayout title="Role Details" icon={Settings}>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
            <p className="text-sm text-gray-600">Loading role details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!role) {
    return null;
  }

  return (
    <PageLayout
      title="Role Details"
      icon={Settings}
      actions={
        <>
          <Button
            variant="secondary"
            icon={Pencil}
            onClick={() => navigate(`/roles/edit/${roleId}`)}
          >
            Edit Role
          </Button>
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/roles')}>
            Back to Roles
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <DetailCard title={role.role_name}>
          <DetailRow label="Role ID" value={role.id} />
          <DetailRow label="Role Name" value={role.role_name} />
        </DetailCard>

        <DetailCard title="System Information">
          <DetailRow label="Created By" value={role.created_by || 'System'} />
          <DetailRow label="Created At" value={formatDate(role.created_at)} />
          <DetailRow label="Last Updated By" value={role.updated_by || 'N/A'} />
          <DetailRow label="Last Updated At" value={formatDate(role.updated_at)} />
        </DetailCard>

        <DetailCard title="Additional Information">
          <div className="bg-brand-light border border-blue-200 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <span className="text-xl sm:text-2xl flex-shrink-0">ℹ️</span>
              <div>
                <h4 className="font-semibold text-sm sm:text-base text-gray-900 mb-1">About Roles</h4>
                <p className="text-xs sm:text-sm text-gray-700">
                  Roles define the access level and permissions for users in the system.
                  Each user is assigned a role that determines what features and data they can access.
                </p>
              </div>
            </div>
          </div>
        </DetailCard>
      </div>
    </PageLayout>
  );
};

export default ViewRole;
